import React, { useState } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import FieldEditHistory from '../ui/FieldEditHistory';

export const BANK_FIELD_LABELS = {
  accountNumber: 'Account number',
  ifsc: 'IFSC',
  holderName: 'Name on account',
  bankName: 'Bank',
};

// What each review reason means to the recruiter checking the payout details.
const REVIEW_REASON_TEXT = {
  NAME_NOT_CONFIRMED: 'We could not confirm the account holder name on the document',
  NAME_CONFLICT: 'The document names a different account holder as well as the candidate',
  ACCOUNT_NOT_READ: 'The account number could not be read from the document — typed by the candidate',
  ACCOUNT_DIFFERS_FROM_DOCUMENT: 'The candidate changed the account number read from the document',
  IFSC_NOT_READ: 'The IFSC could not be read from the document',
  IFSC_NOT_PROVIDED: 'No IFSC was provided',
  IFSC_SUGGESTED: 'The IFSC was worked out from the MICR code, not read directly',
  IFSC_FROM_MICR_CONFIRMED_BY_CANDIDATE: 'The IFSC was worked out from the MICR code and confirmed by the candidate',
  IFSC_ENTERED_BY_CANDIDATE: 'The IFSC was typed by the candidate (not readable on the document)',
  IFSC_DIFFERS_FROM_DOCUMENT: 'The candidate changed the IFSC read from the document',
  HOLDER_NAME_DIFFERS_FROM_DOCUMENT: 'The account name the candidate entered differs from the document',
  HOLDER_NAME_DIFFERS_FROM_APPLICATION: 'The account name differs from the name the candidate applied with',
  BANK_NAME_MISMATCH: 'The bank printed on the document is not the bank of this IFSC',
};

/**
 * One uploaded bank submission's details as stored, or — for uploads made before
 * candidates confirmed every field — rebuilt from the account number and the document
 * read, so older rows still show what is known.
 */
export function bankDetailsOf(uploaded) {
  if (uploaded?.bankDetails) return uploaded.bankDetails;
  const read = uploaded?.verificationDetails?.bank || {};
  return {
    accountNumber: uploaded?.documentNumber || '',
    ifsc: read.ifsc || '',
    bankName: read.bankName || '',
    holderName: '',
    edited: {},
    fromDocument: { holderName: read.accountHolderName || null },
    needsReview: uploaded?.verificationStatus !== 'verified',
    reviewReasons: [],
    legacy: true,
  };
}

function Field({ label, value, mono, edited, hrEdited, copy: CopyButton }) {
  return (
    <div className="bg-white/80 rounded-xl p-2.5 border border-slate-200 min-w-0">
      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex flex-wrap items-center gap-1">
        {label}
        {edited && (
          <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-800 normal-case tracking-normal">
            <PencilSquareIcon className="h-2.5 w-2.5" /> edited by candidate
          </span>
        )}
        {hrEdited && (
          <span className="inline-flex items-center gap-0.5 rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-bold text-sky-800 normal-case tracking-normal">
            <PencilSquareIcon className="h-2.5 w-2.5" /> edited by HR
          </span>
        )}
      </span>
      <div className="flex items-center justify-between gap-1 mt-0.5">
        <span className={`${mono ? 'font-mono tracking-wide' : ''} font-bold text-slate-900 text-sm break-all select-all`}>
          {value || '—'}
        </span>
        {value && CopyButton ? <CopyButton value={value} /> : null}
      </div>
    </div>
  );
}

const INPUT_CLASS =
  'mt-1 block w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 focus:border-[#007BFF] focus:outline-none focus:ring-1 focus:ring-[#007BFF]';

/** HR's edit form for the three bank fields; the bank and branch follow the IFSC on the server. */
function BankEditForm({ details, onSave, onCancel }) {
  const [form, setForm] = useState({
    accountNumber: details.accountNumber || '',
    ifsc: details.ifsc || '',
    holderName: details.holderName || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (field, transform = (v) => v) => (e) => setForm((f) => ({ ...f, [field]: transform(e.target.value) }));

  const submit = async (e) => {
    e.preventDefault();
    // Only changed fields are sent, so an untouched one is never re-validated or logged.
    const changed = {};
    Object.keys(form).forEach((k) => {
      if (form[k].trim() !== String(details[k] || '')) changed[k] = form[k].trim();
    });
    if (!Object.keys(changed).length) {
      onCancel();
      return;
    }
    setSaving(true);
    const ok = await onSave(changed);
    setSaving(false);
    if (ok) onCancel();
  };

  return (
    <form onSubmit={submit} className="space-y-2.5" data-testid="bank-edit-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
        <label className="font-semibold text-slate-600">
          Account number
          <input value={form.accountNumber} onChange={set('accountNumber')} inputMode="numeric" className={`${INPUT_CLASS} font-mono`} />
        </label>
        <label className="font-semibold text-slate-600">
          IFSC
          <input value={form.ifsc} onChange={set('ifsc', (v) => v.toUpperCase())} maxLength={11} className={`${INPUT_CLASS} font-mono`} />
        </label>
        <label className="font-semibold text-slate-600">
          Name on account
          <input value={form.holderName} onChange={set('holderName')} maxLength={100} className={INPUT_CLASS} />
        </label>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={saving} className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-white disabled:opacity-50">
          Cancel
        </button>
        <button type="submit" disabled={saving} className="rounded-lg bg-[#007BFF] px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-[#0062CC] disabled:opacity-60">
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}

/**
 * The recruiter's view of a bank submission: every field, what changed, and why it needs a look.
 * `onSave(fields)` (Go Colors) adds an Edit button; it resolves true when the server accepted.
 */
export default function BankDetailsPanel({ uploaded, CopyButton, onSave }) {
  const [editing, setEditing] = useState(false);
  const details = bankDetailsOf(uploaded);
  const edited = details.edited || {};
  const hrEdited = details.hrEdited || {};
  // A recruiter's correction was not checked against the document, so it is not "verified".
  const verified = uploaded?.verificationStatus === 'verified' && !details.needsReview && !Object.keys(hrEdited).length;
  const branch = [details.branch, details.city].filter(Boolean).join(', ');
  const fromDocument = details.fromDocument || {};

  return (
    <div className={`mt-3 rounded-2xl border p-3.5 space-y-2.5 shadow-sm ${verified ? 'border-emerald-200 bg-emerald-50/60' : 'border-amber-200 bg-amber-50/50'}`}>
      <div className="flex items-center justify-between gap-2">
        <div className={`flex items-center gap-2 text-xs font-bold ${verified ? 'text-emerald-900' : 'text-amber-900'}`}>
          {verified
            ? <CheckCircleIcon className="h-4 w-4 shrink-0" />
            : <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />}
          <span>{verified ? 'Bank details verified against the document' : 'Bank details — please review'}</span>
        </div>
        {onSave && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid="bank-edit-btn"
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <PencilSquareIcon className="h-3.5 w-3.5" /> Edit
          </button>
        )}
      </div>

      {editing ? (
        <BankEditForm details={details} onSave={onSave} onCancel={() => setEditing(false)} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-xs">
          <Field label="Account number" value={details.accountNumber} mono edited={edited.accountNumber} hrEdited={hrEdited.accountNumber} copy={CopyButton} />
          <Field label="IFSC" value={details.ifsc} mono edited={edited.ifsc} hrEdited={hrEdited.ifsc} copy={CopyButton} />
          <Field label="Name on account" value={details.holderName} edited={edited.holderName} hrEdited={hrEdited.holderName} copy={CopyButton} />
          <Field label="Bank" value={details.bankName} />
          {branch && <Field label="Branch" value={branch} />}
        </div>
      )}

      {(edited.accountNumber || edited.ifsc || edited.holderName) && (
        <p className="text-[11px] text-slate-600">
          Read from the document:{' '}
          {[
            edited.accountNumber && fromDocument.accountNumber && `account ${fromDocument.accountNumber}`,
            edited.ifsc && fromDocument.ifsc && `IFSC ${fromDocument.ifsc}`,
            edited.holderName && (fromDocument.nameOnPage || fromDocument.holderName) &&
              `name “${fromDocument.nameOnPage || fromDocument.holderName}”`,
          ].filter(Boolean).join(' · ') || '—'}
        </p>
      )}

      {details.reviewReasons?.length > 0 && (
        <ul className="text-[11px] text-amber-900/90 space-y-0.5 list-disc pl-4">
          {details.reviewReasons.map((code) => (
            <li key={code}>{REVIEW_REASON_TEXT[code] || code}</li>
          ))}
        </ul>
      )}
      {details.legacy && (
        <p className="text-[11px] text-slate-500">
          Uploaded before candidates confirmed every bank field — IFSC and bank are as read from the document. Check the document below.
        </p>
      )}
      <FieldEditHistory entries={uploaded?.hrEdits} labels={BANK_FIELD_LABELS} />
    </div>
  );
}
